import { useFeatureQuery } from '@common/hooks/query/useFeatureQuery';
import {
    BaseFeatureArgs,
    FixedFilterGenerator,
    InitialQueryState,
    QueryManagerOptions,
    SkipOrArgs,
} from '@common/types/queryState';
import { composeGenerators } from '@common/utils/query/composeGenerators';
import { useSelectedProductionId } from '@features/productions';
import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';
import { useGetCollectionsQuery } from '../api';
import { COLLECTION_KEYS } from '../mappers';
import { GetCollectionsArgs } from '../types';

export type UseCollectionsArgs = BaseFeatureArgs;

const collectionsFilterGenerator: FixedFilterGenerator<GetCollectionsArgs> = (
    _args,
) => {
    return [
        'type:(Notebook OR Story OR Episode) OR funnel:(Delivery.Series)',
        '-title_lc:zz_frontOfficeData',
    ];
};

export const useCollections = (
    initialArgs: InitialQueryState = {},
    args?: SkipOrArgs<UseCollectionsArgs>,
    options?: QueryManagerOptions,
) => {
    const productionId = useSelectedProductionId();

    const queryArgs: SkipOrArgs<GetCollectionsArgs> = useMemo(() => {
        if (args === skipToken || !productionId) return skipToken;

        const mergedGenerators = composeGenerators(
            collectionsFilterGenerator,
            args?.fixedFilterGenerators,
        );

        return {
            ...args,
            productionId,
            facetFields: [],
            searchTargetFields: ['title_lc'],
            keyMap: COLLECTION_KEYS,
            fixedFilterGenerators: mergedGenerators,
        } as GetCollectionsArgs;
    }, [args, productionId]);

    return useFeatureQuery(
        { sorting: [{ id: 'title', desc: false }], ...initialArgs },
        useGetCollectionsQuery,
        queryArgs,
        options,
    );
};
